import * as React from "react";
import Link from "next/link";
import { IGroup } from "@nju/data-access-portal-dashboard";
import { useOfferMode } from "@nju/data-access-portal-offers";
import {
  IMobileOrchestrator,
  IMultiOrderOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { portalRoutes } from "@nju/routes";
import { A } from "@nju/ui";
import { formatPhoneNumber, POSITIVE_DIGITS } from "@nju/utils";
import { Button } from "./Button";
import { resolveShowAddNumberButton } from "./utils";

interface IMsisdnSwitcherCommonProps {
  handleSetSelectedNumber: (userId: string) => void;
  handleNewOrder: () => void;
  orchestrator: IMobileOrchestrator | IMultiOrderOrchestrator | undefined;
  groups: IGroup[] | undefined;
  isCompleted: boolean;
  isLoading: boolean;
}

interface IMsisdnSwitcherDashboardProps extends IMsisdnSwitcherCommonProps {
  context: "DASHBOARD";
  flow: "multiOrder" | undefined;
  handleSetMultiDuoTrioContext: (flow: "multiOrder") => void;
  multiOrderOrchestrator: IMultiOrderOrchestrator | undefined;
}

interface IMsisdnSwitcherServiceProps extends IMsisdnSwitcherCommonProps {
  context: "SERVICE";
  flow?: undefined;
  handleSetMultiDuoTrioContext?: undefined;
  multiOrderOrchestrator?: undefined;
}

type IMsisdnSwitcherProps =
  | IMsisdnSwitcherDashboardProps
  | IMsisdnSwitcherServiceProps;

export function MsisdnSwitcher({
  handleSetSelectedNumber,
  handleSetMultiDuoTrioContext,
  handleNewOrder,
  flow,
  context,
  groups,
  isLoading,
  orchestrator,
  multiOrderOrchestrator,
}: IMsisdnSwitcherProps) {
  const mode = useOfferMode();
  const currentOfferCategory: IGroup["members"][number]["offerCategory"] =
    mode === "mobile" ? "MOBILE" : "MOBILE_POSTPAID";

  if (!groups) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const showAddNumberButon = resolveShowAddNumberButton({
    groups,
    multiOrderOrchestrator,
    currentOfferCategory,
  });
  const members = groups[0].members.filter((member) => {
    return (
      member.offerCategory === currentOfferCategory ||
      (mode === "mobile" && !member.offerCategory)
    );
  });

  return (
    <div
      className="
        flex mb-3 gap-2 relative items-center scrollbar-width-none webkit-scrollbar-none ms-overflow-style-none
        only-mobile:w-[calc(100vw-45px)] only-mobile:overflow-y-hidden only-mobile:whitespace-nowrap
        only-mobile:h-[50px]
      "
      data-cy="msisdn-switcher"
    >
      <>
        {members.map((member, index) => {
          return (
            <React.Fragment key={`number-${index + 1}`}>
              {context === "DASHBOARD" &&
                member.id &&
                (member?.selectedMsisdn || member?.primaryMsisdn) && (
                  <Link
                    href={portalRoutes.mojeKonto.index.get({
                      mode,
                      userId: member.id,
                    })}
                    passHref={true}
                  >
                    <A
                      display="inline"
                      isDisabled={isLoading}
                      onClick={() => {
                        if (member.id !== orchestrator?.userId || flow) {
                          handleSetSelectedNumber(member.id as string);
                        }
                      }}
                      size="inline"
                      testId="label-with-number"
                      variant="neutral"
                    >
                      <Button
                        badge={member?.selectedMsisdn ? "w trakcie" : undefined}
                        isActive={member.id === orchestrator?.userId && !flow}
                        label={formatPhoneNumber(
                          member?.primaryMsisdn
                            ? member?.primaryMsisdn
                            : member?.selectedMsisdn,
                          POSITIVE_DIGITS,
                          " ",
                          "",
                          true,
                        )}
                      />
                    </A>
                  </Link>
                )}
            </React.Fragment>
          );
        })}
        {context === "DASHBOARD" &&
          multiOrderOrchestrator &&
          (multiOrderOrchestrator.offerCategory === currentOfferCategory ||
            (mode === "mobile" && !multiOrderOrchestrator.offerCategory)) &&
          multiOrderOrchestrator.state !== "START" && (
            <Link
              href={portalRoutes.mojeKonto.index.get({
                mode,
                flow: "multiOrder",
              })}
              passHref={true}
            >
              <A
                display="inline"
                isDisabled={isLoading}
                onClick={() => {
                  handleSetMultiDuoTrioContext("multiOrder");
                }}
                size="inline"
                variant="neutral"
              >
                <Button
                  badge="w trakcie"
                  icon={{
                    name: "file-edit",
                    tone: flow ? "secondary" : "accent",
                  }}
                  isActive={!!flow}
                  label="zamówienie"
                />
              </A>
            </Link>
          )}
      </>
      {members.map((member, index) => {
        return (
          <React.Fragment key={`number-${index + 1}`}>
            {context === "SERVICE" && member.primaryMsisdn && member.id && (
              <Link
                href={portalRoutes.mojeKonto.mojeUslugi.index.get({
                  mode: "mobile",
                  userId: member.id,
                })}
                passHref={true}
              >
                <A
                  display="inline"
                  isDisabled={isLoading}
                  onClick={() => {
                    if (member.id !== orchestrator?.userId) {
                      handleSetSelectedNumber(member.id as string);
                    }
                  }}
                  size="inline"
                  variant="neutral"
                >
                  <Button
                    badge={undefined}
                    isActive={member.id === orchestrator?.userId}
                    label={formatPhoneNumber(
                      member?.primaryMsisdn,
                      POSITIVE_DIGITS,
                      " ",
                      "",
                      true,
                    )}
                  />
                </A>
              </Link>
            )}
          </React.Fragment>
        );
      })}
      {showAddNumberButon && (
        <button onClick={handleNewOrder}>
          <Button
            badge={undefined}
            icon={{
              name: "plus-circle",
              tone: "accent",
            }}
            isActive={false}
            label="dodaj numer"
          />
        </button>
      )}
    </div>
  );
}
